import type { Effort, Priority, Task, TaskStatus } from '../types'

export type ImportedTask = Omit<Task, 'id' | 'createdAt'>

const baseTask = (): ImportedTask => ({
  title: '', description: '', status: 'todo', priority: 'medium', effort: 3,
  isToday: false, tags: [], subtasks: [], comments: [], links: [], projectId: '',
})

const escapeIcs = (value: string) => value.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;')
const unescapeIcs = (value: string) => value.replace(/\\n/gi, '\n').replace(/\\([,;\\])/g, '$1')
const compactDate = (value: string) => value.replace(/-/g, '')
const addDays = (value: string, days: number) => {
  const date = new Date(`${value}T12:00:00`)
  date.setDate(date.getDate() + days)
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
}
const utcStamp = () => new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')

export function tasksToIcs(tasks: Task[]) {
  const events = tasks.filter((task) => task.status !== 'done' && (task.startDate || task.dueDate)).map((task) => {
    const startDate = task.startDate || task.dueDate!
    const endDate = task.dueDate || startDate
    const lines = ['BEGIN:VEVENT', `UID:${task.id}@taskpriority.local`, `DTSTAMP:${utcStamp()}`, `SUMMARY:${escapeIcs(task.title)}`]
    if (task.startTime) {
      lines.push(`DTSTART:${compactDate(startDate)}T${task.startTime.replace(':', '')}00`)
      const [hour, minute] = task.startTime.split(':').map(Number)
      const total = hour * 60 + minute + 60
      const endTime = task.endTime || `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
      const derivedEndDate = !task.endTime && total >= 1440 ? addDays(endDate, 1) : endDate
      lines.push(`DTEND:${compactDate(derivedEndDate)}T${endTime.replace(':', '')}00`)
    } else {
      lines.push(`DTSTART;VALUE=DATE:${compactDate(startDate)}`)
      lines.push(`DTEND;VALUE=DATE:${compactDate(addDays(endDate, 1))}`)
    }
    if (task.description) lines.push(`DESCRIPTION:${escapeIcs(task.description)}`)
    if (task.tags.length) lines.push(`CATEGORIES:${task.tags.map(escapeIcs).join(',')}`)
    lines.push('END:VEVENT')
    return lines.join('\r\n')
  })
  return ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//TaskPriority//Agenda//PT-BR', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', ...events, 'END:VCALENDAR', ''].join('\r\n')
}

export function downloadIcs(tasks: Task[], filename = `taskpriority-agenda-${new Date().toISOString().slice(0, 10)}.ics`) {
  const content = tasksToIcs(tasks)
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const parseDate = (value?: string) => {
  if (!value) return undefined
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`
  const compact = value.match(/^(\d{4})(\d{2})(\d{2})/)
  if (compact) return `${compact[1]}-${compact[2]}-${compact[3]}`
  const brazilian = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (brazilian) {
    const first = Number(brazilian[1]), second = Number(brazilian[2])
    const day = second > 12 ? second : first
    const month = second > 12 ? first : second
    return `${brazilian[3]}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return undefined
  return new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
}

const parseTime = (value?: string) => {
  if (!value) return undefined
  const match = value.match(/T(\d{2}):?(\d{2})/) ?? value.match(/^(\d{2}):(\d{2})/)
  return match ? `${match[1]}:${match[2]}` : undefined
}

export function tasksFromIcs(content: string): ImportedTask[] {
  const unfolded = content.replace(/\r?\n[ \t]/g, '')
  return [...unfolded.matchAll(/BEGIN:(?:VEVENT|VTODO)\r?\n([\s\S]*?)\r?\nEND:(?:VEVENT|VTODO)/gi)].map((match) => {
    const fields = new Map<string, string>()
    for (const line of match[1].split(/\r?\n/)) {
      const separator = line.indexOf(':')
      if (separator < 0) continue
      const property = line.slice(0, separator).toUpperCase()
      const name = property.split(';')[0]
      fields.set(name, line.slice(separator + 1))
      if (property.includes('VALUE=DATE')) fields.set(`${name}_ALLDAY`, 'true')
    }
    const status = fields.get('STATUS')?.toUpperCase() === 'COMPLETED' ? 'done' : 'todo'
    const start = fields.get('DTSTART')
    const end = fields.get('DTEND') || fields.get('DUE')
    return {
      ...baseTask(), title: unescapeIcs(fields.get('SUMMARY') || 'Tarefa importada'),
      description: unescapeIcs(fields.get('DESCRIPTION') || ''), status,
      startDate: parseDate(start), dueDate: fields.has('DTEND_ALLDAY') && parseDate(end) ? addDays(parseDate(end)!, -1) : parseDate(end), startTime: parseTime(start), endTime: parseTime(end),
      completedAt: status === 'done' ? new Date().toISOString() : undefined,
      tags: (fields.get('CATEGORIES') || '').split(',').map(unescapeIcs).map((tag) => tag.trim()).filter(Boolean),
    }
  })
}

function csvRows(content: string): string[][] {
  const firstLine = content.split(/\r?\n/, 1)[0] ?? ''
  const delimiter = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ';' : ','
  const rows: string[][] = []
  let row: string[] = [], field = '', quoted = false
  for (let index = 0; index < content.length; index += 1) {
    const char = content[index]
    if (char === '"' && quoted && content[index + 1] === '"') { field += '"'; index += 1 }
    else if (char === '"') quoted = !quoted
    else if (char === delimiter && !quoted) { row.push(field); field = '' }
    else if ((char === '\n' || char === '\r') && !quoted) {
      if (char === '\r' && content[index + 1] === '\n') index += 1
      row.push(field); if (row.some(Boolean)) rows.push(row); row = []; field = ''
    } else field += char
  }
  row.push(field); if (row.some(Boolean)) rows.push(row)
  return rows
}

const normalizeHeader = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase().replace(/[^a-z0-9]/g, '')
const getField = (record: Record<string, string>, ...names: string[]) => names.map(normalizeHeader).map((name) => record[name]).find((value) => value?.trim())?.trim()

export function tasksFromCsv(content: string): ImportedTask[] {
  const rows = csvRows(content.replace(/^\uFEFF/, ''))
  if (rows.length < 2) return []
  const headers = rows[0].map(normalizeHeader)
  return rows.slice(1).map((row) => Object.fromEntries(headers.map((header, index) => [header, row[index] ?? '']))).map((record) => {
    const rawStatus = getField(record, 'status', 'situação')?.toLowerCase() ?? ''
    const status: TaskStatus = /conclu|complete/.test(rawStatus) ? 'done' : /andamento|progress/.test(rawStatus) ? 'in-progress' : 'todo'
    const rawPriority = getField(record, 'priority', 'prioridade', 'importance')?.toLowerCase() ?? ''
    const priority: Priority = /urgent|urgente/.test(rawPriority) ? 'urgent' : /high|alta/.test(rawPriority) ? 'high' : /low|baixa/.test(rawPriority) ? 'low' : 'medium'
    return {
      ...baseTask(), title: getField(record, 'subject', 'title', 'título', 'assunto') ?? '',
      description: getField(record, 'body', 'description', 'notes', 'descrição', 'anotações') ?? '', status, priority,
      startDate: parseDate(getField(record, 'start date', 'startdate', 'data de início')),
      dueDate: parseDate(getField(record, 'due date', 'duedate', 'data de vencimento')),
      startTime: parseTime(getField(record, 'start time', 'starttime', 'hora de início')),
      endTime: parseTime(getField(record, 'due time', 'end time', 'duetime', 'endtime', 'hora de término')),
      completedAt: status === 'done' ? new Date().toISOString() : undefined,
      tags: (getField(record, 'categories', 'categorias') ?? '').split(/[;,]/).map((tag) => tag.trim()).filter(Boolean),
    }
  }).filter((task) => task.title)
}

export function tasksFromMicrosoftJson(content: string): ImportedTask[] {
  const data = JSON.parse(content)
  const candidates: any[] = Array.isArray(data) ? data : Array.isArray(data.value) ? data.value : Array.isArray(data.tasks) ? data.tasks : []
  return candidates.filter((item) => typeof item?.title === 'string').map((item) => {
    const status: TaskStatus = item.status === 'completed' || item.status === 'done' ? 'done' : item.status === 'inProgress' ? 'in-progress' : 'todo'
    const importance = String(item.importance ?? '').toLowerCase()
    const priority: Priority = importance === 'high' ? 'high' : importance === 'low' ? 'low' : 'medium'
    return {
      ...baseTask(), title: item.title, description: item.body?.content ?? item.description ?? '', status, priority,
      startDate: parseDate(item.startDateTime?.dateTime ?? item.startDate), dueDate: parseDate(item.dueDateTime?.dateTime ?? item.dueDate),
      startTime: parseTime(item.startDateTime?.dateTime), endTime: parseTime(item.dueDateTime?.dateTime),
      completedAt: item.completedDateTime?.dateTime ?? (status === 'done' ? new Date().toISOString() : undefined),
      tags: Array.isArray(item.categories) ? item.categories.map(String) : [],
    }
  })
}
